// ============================================================================
// ui/working-scrim.js — "we're working, sit tight" for the slow, uncancellable jobs
// ============================================================================
// Ryan, 2026-08-07: *"if I go to Download image, it can take 4-5 seconds for the
// pop-up to appear to choose where to save. This delay can cause confusion and for
// people to exit out or click other things… should we block folks from clicking
// anything else while it's downloading? Either visually or just UX-wise, or both?"*
//
// ── BOTH, AND THE FUNCTIONAL HALF IS NOT COSMETIC ────────────────────────────
// Blocking input here prevents a real defect, not just confusion. `portraitPNG`
// STAGES A POSE on the live 3D stage, renders at 3072², crops to the product's
// alpha and then puts the stage back. Anything the buyer does mid-flight races
// that: a second Download starts a concurrent snapshot of a stage the first one is
// still posing, and switching product or plate colour mid-render yields a PNG that
// does not match what they were looking at when they asked for it. Both produce a
// file that is quietly wrong rather than an error anyone would notice.
//   So the input block is the fix and the scrim is what makes it honest. A UI that
// stops responding with no explanation reads as broken — which is the very thing
// Ryan is trying to prevent ("causes people to exit out").
//
// ⚠ DELAY-GATED, mirroring `status-bar.js`'s busy pill and for the same reason: on a
// fast machine the render can finish well inside half a second, and a scrim that
// flashes up and vanishes reads as a glitch. Nothing appears until the work has
// ALREADY outlasted the threshold, so this exists exactly when Ryan's scenario does.
//
// ⛔ THE EXIT IS UNCONDITIONAL, AND THAT IS THE LOAD-BEARING PART. This is a screen
// with no close button covering the whole app, so if it can ever fail to lift, the
// designer is bricked and the buyer's only move is to reload and lose the design.
// Three guarantees, because one is not enough:
//   1. `done()` is idempotent and callers put it in a `finally`, so a throw still lifts it.
//   2. a WATCHDOG lifts it regardless after `MAX_MS`, even if the caller never returns
//      (a hung WebGL readback returns no promise at all — it just never settles).
//   3. it re-entrancy-guards itself, so two overlapping jobs cannot leave one scrim
//      owned by a finished job and another by a live one.
// The watchdog is the one that matters: 1 and 2 both trust the caller, and the
// failure being defended against is precisely the caller never coming back.
// ============================================================================

import { h } from './dom.js';

/** Matches `status-bar.js`'s BUSY_DELAY_MS — one perceptual threshold, not two. */
const SHOW_AFTER_MS = 550;
/**
 * Hard ceiling before the scrim lifts itself and says something went wrong. Chosen
 * well ABOVE the 4-5s Ryan measured (so a genuinely slow machine is never cut off
 * mid-render) and well BELOW the point a buyer concludes the page is dead.
 */
const MAX_MS = 30000;

let active = null; // { el, showTimer, watchdog, root }

/**
 * Cover the designer and swallow input until the returned `done()` is called.
 *
 * @param {HTMLElement} root  the app root (NOT document.body — the scrim's styling
 *   reads `--wd-*` tokens scoped to `.weight-designer`, and a body-appended node
 *   loses every one of them, which is a trap this codebase has hit before).
 * @param {string} message    what is happening, in the buyer's words
 * @param {{onTimeout?: Function}} [opts]
 * @returns {() => void} idempotent teardown — call it in a `finally`
 */
export function showWorking(root, message, opts = {}) {
  if (!root) return () => {};
  /* ⚠ RESOLVE THE `.weight-designer` HOST, don't assume the caller passed it. Two things depend on
     landing exactly there and both fail silently otherwise: the scrim's `--wd-*`/`--dt-*` tokens are
     scoped to that class (a node outside it renders unstyled — transparent, invisible, and still
     swallowing clicks, which is the worst of every world), and it is the element carrying
     `position: relative`, so `inset: 0` on anything else covers the wrong box. */
  const host = (root.closest && root.closest('.weight-designer'))
    || (root.classList && root.classList.contains('weight-designer') ? root : null)
    || (root.querySelector && root.querySelector('.weight-designer'))
    || root;
  root = host;
  // A second job while one is live: adopt the newer message, keep ONE scrim, and
  // hand back a teardown that only the last caller can trip.
  if (active) {
    const label = active.el.querySelector('.wd-working__msg');
    if (label) label.textContent = message;
  } else {
    const el = h('div', {
      class: 'wd-working',
      // Announced, not silent: a screen-reader user has no spinner to look at, and
      // this is the moment they most need to know the app is busy rather than dead.
      role: 'alert',
      'aria-live': 'assertive',
      'aria-busy': 'true',
    },
    h('div', { class: 'wd-working__card wd-flat-glass' },
      h('div', { class: 'wd-working__spinner', 'aria-hidden': 'true' }),
      h('div', { class: 'wd-working__msg' }, message),
      /* The second line is the whole point of Ryan's request — the first line says
         WHAT, this says WHAT TO DO ("just sit tight and wait a few seconds"). */
      h('div', { class: 'wd-working__sub' }, opts.sub || 'This can take a few seconds. Your save dialog will open when it’s ready.'),
      /* ⚠ CANCEL EXISTS ONLY WHEN THE CALLER CAN HONOUR IT. A button that cannot actually stop the
         work is worse than none: it invites a press, does nothing visible, and teaches the buyer the
         app is broken. The single-image download has no abort path and therefore gets no button;
         the multi-product sheet does (`renderProductStills` takes an AbortSignal), so it gets one. */
      opts.onCancel
        ? h('button', {
          type: 'button',
          class: 'wd-btn wd-working__cancel wd-flat-glass wd-flat-glass--flush',
          /* ⛔ IT ABORTS; IT DOES NOT TEAR DOWN. The first cut called `teardown()` right here, which
             lifted the block the instant the button was pressed — while the job was still running.
             Aborting is COOPERATIVE: `renderProductStills` checks the signal between products, so
             the product already in the oven keeps shooting for seconds afterwards. Removing the
             scrim at that moment hands the buyer a live app on top of a live render, which is the
             precise corruption this whole overlay exists to prevent, arrived at through the one
             control meant to be safe.
               So the press aborts and says so, and the scrim comes down where every other exit
             comes down: the caller's `finally`, once the work has actually stopped. */
          onClick: (e) => {
            const btn = e.currentTarget;
            btn.disabled = true;
            setWorkingProgress(opts.cancellingLabel || 'Cancelling…', 'Finishing the current weight, then stopping.');
            try { opts.onCancel(); } catch { /* the caller's finally still lifts it */ }
          },
        }, opts.cancelLabel || 'Cancel')
        : null));

    /* ⚠ THE BLOCK IS IMMEDIATE; ONLY THE PICTURE IS DELAYED. The first cut set `el.hidden = true`
       for the whole delay, which also removed it from the hit-testing tree — so for the first 550ms
       nothing was blocked at all. The Download button was still safe (its own `isWorking()` guard
       fires synchronously), but every OTHER control was live: product switch, plate colour, layout.
       Those are exactly the ones that corrupt an in-flight snapshot, and 550ms is plenty of time to
       reach one. So the element mounts fully interactive-blocking and TRANSPARENT, and `.is-shown`
       fades the backdrop and card in later. Delay the reassurance, never the protection. */
    root.appendChild(el);
    active = { el, root, showTimer: null, watchdog: null };
    active.showTimer = setTimeout(() => { if (active && active.el === el) el.classList.add('is-shown'); }, SHOW_AFTER_MS);
    /* The ceiling is per-CALLER, because the jobs are not the same size. One image is ~5s; a
       ten-product sheet is legitimately minutes on a slow machine, and a watchdog tuned for the
       first would tear the scrim off the second MID-RENDER — handing the buyer a live app while an
       offscreen oven is still shooting, which is precisely the corruption the scrim prevents. */
    const ceiling = Number.isFinite(opts.maxMs) && opts.maxMs > 0 ? opts.maxMs : MAX_MS;
    active.watchdog = setTimeout(() => {
      // The caller never came back. Lift anyway — a wrong-looking app the buyer can
      // still use beats a correct-looking one they cannot.
      const stuck = active;
      teardown();
      try { opts.onTimeout?.(); } catch { /* never let the handler re-brick it */ }
      if (stuck) console.warn('[wd] working scrim timed out after', ceiling, 'ms —', message);
    }, ceiling);
  }

  const mine = active;
  return () => { if (active && active === mine) teardown(); };
}

function teardown() {
  if (!active) return;
  clearTimeout(active.showTimer);
  clearTimeout(active.watchdog);
  active.el.remove();
  active = null;
}

/**
 * Update the live scrim's headline and sub-line — "Rendering 3 of 8".
 *
 * ⚠ A COUNTER IS NOT DECORATION ON A LONG JOB. A spinner says "something is happening"; over thirty
 * seconds a buyer needs "it is moving AND it will end", and only a count of a known total says the
 * second thing. No-op when nothing is showing, so a caller can report progress unconditionally.
 */
export function setWorkingProgress(message, sub) {
  if (!active) return;
  const m = active.el.querySelector('.wd-working__msg');
  const s2 = active.el.querySelector('.wd-working__sub');
  if (m && message) m.textContent = message;
  if (s2 && sub) s2.textContent = sub;
}

/** Is a blocking job in flight? The re-entrancy guard callers use to refuse a second click. */
export function isWorking() {
  return !!active;
}
